import { jsx as _jsx } from "react/jsx-runtime";
import { useSideContent } from '#pplx/frontend/apps/ask/search-side-content/core/ProviderShared.js';
import { FileViewerContext } from '#pplx/frontend/apps/ask/shared-ui-components/fileViewerTypes.js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
function hasNonPdfExtension(fileName) {
    const lastDot = fileName.lastIndexOf('.');
    if (lastDot <= 0 || lastDot >= fileName.length - 1)
        return false;
    return fileName.toLowerCase().slice(lastDot) !== '.pdf';
}
function getFileTabType(file) {
    if (hasNonPdfExtension(file.name)) {
        return 'file_preview';
    }
    return 'pdf';
}
export const FileViewerStateProvider = React.memo(function FileViewerStateProvider({ children }) {
    const [file, setFile] = useState();
    const [openOptions, setOpenOptions] = useState();
    const [openRequestId, setOpenRequestId] = useState(0);
    const { dispatch } = useSideContent();
    const [viewerDispatch, setViewerDispatch] = useState();
    const latestViewerDispatch = useRef(undefined);
    // A ref, not state, so callers can check it after an await, even once they unmount.
    const currentFile = useRef(undefined);
    const blobUrl = useRef(undefined);
    const releaseBlobUrl = useCallback(() => {
        if (blobUrl.current) {
            URL.revokeObjectURL(blobUrl.current);
            blobUrl.current = undefined;
        }
    }, []);
    useEffect(() => {
        return () => releaseBlobUrl();
    }, [releaseBlobUrl]);
    const openFile = useCallback((file, options) => {
        if (blobUrl.current !== file.url) {
            releaseBlobUrl();
        }
        if (options?.isBlobUrl) {
            blobUrl.current = file.url ?? undefined;
        }
        currentFile.current = file;
        setFile(file);
        setOpenOptions(options);
        setOpenRequestId((id) => id + 1);
        const tabType = getFileTabType(file);
        dispatch({ action: 'open', tabType });
    }, [setFile, dispatch, releaseBlobUrl]);
    const closeViewer = useCallback(() => {
        releaseBlobUrl();
        currentFile.current = undefined;
        dispatch({ action: 'close' });
        setFile(undefined);
        setOpenOptions(undefined);
        setViewerDispatch(undefined);
        latestViewerDispatch.current = undefined;
    }, [setFile, setViewerDispatch, dispatch, releaseBlobUrl]);
    const isCurrentFile = useCallback((file) => currentFile.current === file, []);
    const loadedFile = useRef(undefined);
    const markFileLoaded = useCallback((file, loaded) => {
        if (loaded) {
            loadedFile.current = file;
        }
        else if (loadedFile.current === file) {
            loadedFile.current = undefined;
        }
    }, []);
    const getLoadedFile = useCallback(() => (loadedFile.current === currentFile.current ? loadedFile.current : undefined), []);
    const pendingActions = useRef([]);
    // We can have the case where the user calls openFile and then doViewerAction,
    // so we have to make this callback not really depend on any React state.
    const doViewerAction = useCallback((action) => {
        if (latestViewerDispatch.current) {
            latestViewerDispatch.current(action);
        }
        else {
            pendingActions.current.push(action);
        }
    }, []);
    const _setViewerDispatch = useCallback((dispatch) => {
        setViewerDispatch(() => dispatch);
        latestViewerDispatch.current = dispatch;
        if (pendingActions.current.length > 0 && dispatch) {
            pendingActions.current.forEach((action) => dispatch(action));
            pendingActions.current = [];
        }
    }, [setViewerDispatch]);
    const contextValue = useMemo(() => ({
        file,
        openOptions,
        openRequestId,
        canOpenFiles: true,
        openFile,
        closeViewer,
        isCurrentFile,
        markFileLoaded,
        getLoadedFile,
        doViewerAction,
        isViewerOpen: !!viewerDispatch,
        setViewerDispatch: _setViewerDispatch,
    }), [
        file,
        openOptions,
        openRequestId,
        openFile,
        closeViewer,
        isCurrentFile,
        markFileLoaded,
        getLoadedFile,
        doViewerAction,
        viewerDispatch,
        _setViewerDispatch,
    ]);
    return _jsx(FileViewerContext.Provider, { value: contextValue, children: children });
});
//# sourceMappingURL=FileViewerStateProvider.js.map